/**
 * About page content — 100% fidelity to content/company/about.md
 * Cross-reference: content/company/about.md
 * 20 sections, all handcrafted from source.
 */

export const aboutMeta = {
  title: "About CryptoMize | Perception X2 Platform",
  description:
    "CryptoMize is the organization behind Perception X2. 15+ years of autonomous perception amplification excellence. 300+ elite clients. Nine proprietary AI engines. Built on precision, discretion, and measurable impact.",
  slug: "about",
  canonical: "https://perception.ac/about",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  ctaPrimary: { label: string; href: string };
  ctaSecondary: { label: string; href: string };
  trustIndicators: string[];
}

export const hero: HeroData = {
  eyebrow: "About CryptoMize",
  title: "CryptoMize. The Organization Behind",
  titleAccent: "Perception X2.",
  lede: "CryptoMize is the company that built and operates Perception X2 — the world's most advanced autonomous perception amplification platform. For over 15 years, CryptoMize has engineered systems that shape how organizations are perceived across digital landscapes. Our work sits at the intersection of artificial intelligence, cognitive science, and strategic communications.",
  ctaPrimary: { label: "Explore the Platform", href: "/platform/overview" },
  ctaSecondary: { label: "Meet the Team", href: "#leadership" },
  trustIndicators: [
    "15+ Years of Operational Excellence",
    "300+ Elite Global Clients",
    "Nine Proprietary AI Engines",
    "Zero Security Incidents · 99.9999% Uptime",
  ],
};

export interface OverviewData {
  eyebrow: string;
  title: string;
  paragraphs: string[];
  pillars: { label: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const overview: OverviewData = {
  eyebrow: "Company Overview",
  title: "Who We Are. What We Built.",
  paragraphs: [
    "CryptoMize is a technology organization dedicated to one mission: autonomous perception amplification at global scale. We are not a marketing agency. We are not a PR firm. We are an engineering company that builds and operates the most advanced AI-powered perception platform in existence.",
    "CryptoMize designs, develops, and operates Perception X2 — a platform that processes millions of data points daily across 50+ digital platforms. Our nine proprietary AI engines generate 300–500% improvements in narrative penetration while maintaining 95–98% organic authenticity. These are not marketing claims. These are measured, verifiable outcomes.",
    "We operate with the precision of a defense contractor and the innovation velocity of a technology pioneer. Every deployment undergoes rigorous testing. Every strategy is validated by data. Every client relationship is built on measurable results — not promises.",
  ],
  pillars: [
    { label: "What We Do", body: "Engineer autonomous perception amplification at global scale." },
    { label: "How We Work", body: "Data-validated strategy, defense-grade discipline, technology-pioneer velocity." },
    { label: "What Makes Us Different", body: "Proprietary technology that cannot be licensed, replicated, or purchased elsewhere." },
  ],
  cta: [
    { label: "View the Platform", href: "/platform/overview" },
    { label: "Contact Us", href: "/#contact" },
  ],
};

export interface StoryData {
  eyebrow: string;
  title: string;
  acts: { label: string; title: string; body: string }[];
}

export const story: StoryData = {
  eyebrow: "Our Story",
  title: "From Vision to Global Platform.",
  acts: [
    {
      label: "The Founding Insight",
      title: "Perception is engineered, not received.",
      body: "Our founders observed that the organizations with the strongest digital presence were not simply producing more content — they were engineering perception systematically. The gap between those who could afford to engineer perception and those who could not was widening daily.",
    },
    {
      label: "Building the Platform",
      title: "Nine engines. One platform.",
      body: "CryptoMize spent years developing the proprietary AI engines that would become Perception X2. Each engine addressed a specific dimension — from organic simulation to cognitive resonance mapping to echo chamber architecture. The result: autonomous amplification across 50+ platforms with 95–98% organic authenticity.",
    },
    {
      label: "The Outcome",
      title: "300+ elite clients. Continuous refinement.",
      body: "Today, Perception X2 serves 300+ elite clients across governments, enterprises, and global institutions. The platform processes millions of data points daily, delivering 300–500% improvements in narrative penetration. CryptoMize continues to push the boundaries of what autonomous perception amplification can achieve.",
    },
  ],
};

export interface WhatWeDoData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: {
    title: string;
    body: string;
    icon: string;
    accent: "crimson" | "purple" | "cyan" | "emerald" | "amber" | "denim";
  }[];
  cta: { label: string; href: string }[];
}

export const whatWeDo: WhatWeDoData = {
  eyebrow: "What We Do",
  title: "Autonomous Perception Amplification. Defined.",
  intro: "CryptoMize builds and operates Perception X2 — an autonomous perception amplification platform that shapes how organizations are perceived across digital landscapes. Our work encompasses four core capabilities:",
  pillars: [
    {
      title: "Perception Engineering",
      body: "Coordinated deployment across 50+ digital platforms. Search results, social media, news coverage, and content ecosystems shaped to deliver measurable narrative outcomes. Every deployment autonomous, adaptive, organic.",
      icon: "Cpu",
      accent: "crimson",
    },
    {
      title: "Reputation Protection",
      body: "Proactive monitoring, rapid response, and AI-driven threat detection. Our systems identify emerging reputation risks before they escalate, enabling preemptive action conventional monitoring cannot achieve.",
      icon: "ShieldCheck",
      accent: "denim",
    },
    {
      title: "Narrative Amplification",
      body: "AI-generated content intelligence, 144-dimensional emotional resonance mapping, and coordinated deployment. Result: 300–500% improvement in narrative penetration with 95–98% organic authenticity.",
      icon: "Megaphone",
      accent: "purple",
    },
    {
      title: "Strategic Intelligence",
      body: "Real-time analytics, competitive monitoring, and predictive modeling. Organizations using Perception X2 gain visibility into perception dynamics competitors and stakeholders cannot access.",
      icon: "Telescope",
      accent: "emerald",
    },
  ],
  cta: [
    { label: "Explore the Platform", href: "/platform/overview" },
    { label: "Request a Demo", href: "/#contact" },
  ],
};

export interface ValuesData {
  eyebrow: string;
  title: string;
  intro: string;
  values: {
    title: string;
    principle: string;
    body: string;
    glyph: string;
    accent: "crimson" | "purple" | "cyan" | "emerald" | "amber";
  }[];
  footnote: { heading: string; body: string };
  cta: { label: string; href: string }[];
}

export const values: ValuesData = {
  eyebrow: "Our Values",
  title: "Precision. Discretion. Impact.",
  intro: "CryptoMize operates on three non-negotiable values that define every decision, every deployment, and every client relationship.",
  values: [
    {
      title: "Precision",
      principle: "We measure outcomes, not activity.",
      body: "Every strategy is validated by data. Every deployment is monitored in real time. Every result is quantified and reported. Precision is not aspirational at CryptoMize — it is operational.",
      glyph: "crosshair",
      accent: "crimson",
    },
    {
      title: "Discretion",
      principle: "Confidentiality is a capability, not a policy.",
      body: "We serve organizations where confidentiality is not optional. Every engagement operates under strict NDA protection. Every team member undergoes security clearance. Every system is hardened against intrusion.",
      glyph: "lock",
      accent: "purple",
    },
    {
      title: "Impact",
      principle: "300–500%. 95–98%. 99.9999%. The baseline.",
      body: "We deliver measurable results. 300–500% improvement in narrative penetration. 95–98% organic authenticity. 99.9999% uptime. These are not benchmarks we aspire to — they are standards we maintain.",
      glyph: "bolt",
      accent: "amber",
    },
  ],
  footnote: {
    heading: "Beyond the Core",
    body: "Beyond our core principles, CryptoMize commits to ethical deployment, regulatory compliance, and responsible innovation. Every capability we develop undergoes rigorous ethical review. Every deployment complies with applicable regulations. Every outcome is designed to benefit both our clients and the broader digital ecosystem.",
  },
  cta: [
    { label: "See Our Impact", href: "#impact" },
    { label: "Contact Us", href: "/#contact" },
  ],
};

export interface PlatformSummaryData {
  eyebrow: string;
  title: string;
  intro: string;
  engines: { name: string; description: string; code: string }[];
  coverage: { label: string; value: string }[];
  outcomes: { label: string; value: string }[];
  cta: { label: string; href: string }[];
}

export const platformSummary: PlatformSummaryData = {
  eyebrow: "The Platform",
  title: "Perception X2. The Technology That Defines Us.",
  intro: "Perception X2 is the platform that CryptoMize was built to create. It represents 15+ years of proprietary research, engineering, and operational refinement. No other organization has built what we have built. No other platform delivers what Perception X2 delivers.",
  engines: [
    { code: "OSE", name: "Organic Simulation Engine", description: "Generates content statistically indistinguishable from organic engagement." },
    { code: "NGE", name: "Narrative Generation Engine", description: "Creates and deploys narrative strategies across platforms." },
    { code: "CRE", name: "Cognitive Resonance Engine", description: "Maps 144-dimensional emotional resonance data." },
    { code: "ECA", name: "Echo Chamber Architecture", description: "Coordinates reinforcement across platform ecosystems." },
    { code: "AOE", name: "Autonomous Operations Engine", description: "Orchestrates cross-platform deployment without human intervention." },
  ],
  coverage: [
    { label: "Search Engines", value: "Full coverage" },
    { label: "Social Platforms", value: "All major networks" },
    { label: "News & Media", value: "Global outlets" },
    { label: "Forums & Reviews", value: "Verified ecosystems" },
    { label: "Emerging Surfaces", value: "Next-gen platforms" },
  ],
  outcomes: [
    { label: "Narrative Penetration", value: "300–500%" },
    { label: "Organic Authenticity", value: "95–98%" },
    { label: "Platform Uptime", value: "99.9999%" },
  ],
  cta: [
    { label: "Explore the Platform", href: "/platform/overview" },
    { label: "Request a Demo", href: "/#contact" },
  ],
};

export interface ClientsData {
  eyebrow: string;
  title: string;
  intro: string;
  segments: {
    code: string;
    label: string;
    description: string;
    accent: "crimson" | "denim" | "emerald" | "amber" | "purple" | "violet";
  }[];
  whyChoose: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const clients: ClientsData = {
  eyebrow: "Our Clients",
  title: "Served by CryptoMize. Powered by Perception X2.",
  intro: "CryptoMize serves organizations where perception determines outcomes. Our client base spans governments, Fortune 500 enterprises, global institutions, and public figures across multiple continents.",
  segments: [
    { code: "GS", label: "Government & Sovereign Entities", description: "National narrative management, public trust, diplomatic communication.", accent: "crimson" },
    { code: "GE", label: "Global Enterprises", description: "Brand reputation, competitive positioning, market perception.", accent: "denim" },
    { code: "PO", label: "Political Organizations", description: "Campaign perception, voter sentiment, public discourse.", accent: "violet" },
    { code: "PF", label: "Public Figures & HNWIs", description: "Personal brand protection, privacy, legacy management.", accent: "amber" },
    { code: "DI", label: "Defense & Intelligence", description: "Strategic communication, information operations support.", accent: "emerald" },
    { code: "FI", label: "Financial Institutions", description: "Market perception, regulatory positioning, investor relations.", accent: "purple" },
  ],
  whyChoose: [
    { title: "Proprietary Technology", body: "Nine engines. Not licensed, not white-labeled — built, tested, proven." },
    { title: "Measurable Outcomes", body: "300–500% penetration, 95–98% authenticity — quantified, reported, verified." },
    { title: "Absolute Discretion", body: "NDA-grade confidentiality across every engagement, every touchpoint." },
  ],
  cta: [
    { label: "Join Our Client Roster", href: "/#contact" },
    { label: "View Capabilities", href: "/platform/overview" },
  ],
};

export interface TeamData {
  eyebrow: string;
  title: string;
  intro: string;
  divisions: { title: string; body: string; icon: string }[];
  byTheNumbers: { label: string; value: string }[];
  cta: { label: string; href: string }[];
}

export const team: TeamData = {
  eyebrow: "Our Team",
  title: "The People Behind the Platform.",
  intro: "CryptoMize's team represents the intersection of AI engineering, cognitive science, strategic communications, and operational excellence. Our people are not ordinary technologists — they are specialists in perception engineering, narrative science, and autonomous systems.",
  divisions: [
    {
      title: "Engineering Excellence",
      body: "Our engineering team builds and maintains the nine proprietary AI engines that power Perception X2. These are not off-the-shelf solutions — they are custom-built systems refined through years of operational deployment.",
      icon: "Cpu",
    },
    {
      title: "Strategic Intelligence",
      body: "Our strategic team translates platform capability into client outcomes. Every strategy is tailored to organizational objectives, market dynamics, and competitive positioning. We do not apply templates — we engineer solutions.",
      icon: "Telescope",
    },
    {
      title: "Operational Discipline",
      body: "Our operations team ensures 99.9999% uptime, zero security incidents, and consistent delivery across 300+ clients. Operational discipline is not a department at CryptoMize — it is a culture.",
      icon: "ShieldCheck",
    },
  ],
  byTheNumbers: [
    { label: "Specialists in AI Engineering", value: "200+" },
    { label: "Cognitive Science Researchers", value: "50+" },
    { label: "Strategic Communications Experts", value: "80+" },
    { label: "Operational Engineers", value: "120+" },
  ],
  cta: [
    { label: "Meet Our Leadership", href: "#leadership" },
    { label: "Explore Careers", href: "#careers" },
  ],
};

export interface ImpactData {
  eyebrow: string;
  title: string;
  intro: string;
  performanceMetrics: { label: string; value: string; unit: string }[];
  clientOutcomes: { title: string; body: string }[];
  industryImpact: { heading: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const impact: ImpactData = {
  eyebrow: "Our Impact",
  title: "Measurable Outcomes. Proven Results.",
  intro: "CryptoMize measures everything. Every strategy, every deployment, every client engagement is tracked, quantified, and reported. We do not claim impact — we prove it.",
  performanceMetrics: [
    { label: "Narrative Penetration Improvement", value: "300", unit: "to 500%" },
    { label: "Organic Authenticity Rating", value: "95", unit: "to 98%" },
    { label: "Platform Uptime", value: "99.9999", unit: "%" },
    { label: "Security Incidents", value: "0", unit: "in 15+ years" },
    { label: "Platform Coverage", value: "50", unit: "+ ecosystems" },
    { label: "Data Points Processed Daily", value: "M", unit: "illions" },
  ],
  clientOutcomes: [
    { title: "Search Dominance", body: "Top-3 positioning for primary narratives across major search engines." },
    { title: "Narrative Control", body: "Coordinated ownership of how key stories unfold across digital surfaces." },
    { title: "Reputation Resilience", body: "Preemptive threat neutralization before conventional monitoring can detect." },
    { title: "Competitive Positioning", body: "Sustained perception advantage that compounds over time." },
  ],
  industryImpact: [
    { heading: "Defined the Category", body: "CryptoMize did not enter the perception management industry — we created it. Our platform sets the standard others attempt to replicate." },
    { heading: "Set the Standard", body: "Our innovations have shaped the conversation. Our research has informed the field. Our outcomes have defined what is achievable." },
  ],
  cta: [
    { label: "View Our Results", href: "/platform/overview" },
    { label: "Contact Us", href: "/#contact" },
  ],
};

export interface ApproachData {
  eyebrow: string;
  title: string;
  intro: string;
  phases: {
    number: string;
    title: string;
    body: string;
    deliverables: string[];
  }[];
  cta: { label: string; href: string }[];
}

export const approach: ApproachData = {
  eyebrow: "Our Approach",
  title: "How CryptoMize Delivers Results.",
  intro: "CryptoMize follows a disciplined approach to every engagement — from initial consultation through deployment, monitoring, and optimization.",
  phases: [
    {
      number: "01",
      title: "Assessment",
      body: "We begin every engagement with a comprehensive perception audit. This assessment maps your current digital landscape, identifies opportunities and threats, and establishes baseline metrics for measuring improvement.",
      deliverables: ["Perception Landscape Map", "Threat & Opportunity Index", "Baseline Metrics", "Competitive Position Report"],
    },
    {
      number: "02",
      title: "Strategy",
      body: "Based on the assessment, our strategic team designs a custom perception amplification strategy. This strategy addresses platform-specific dynamics, audience segmentation, narrative architecture, and competitive positioning.",
      deliverables: ["Custom Amplification Strategy", "Audience Segmentation", "Narrative Architecture", "Platform-Specific Tactics"],
    },
    {
      number: "03",
      title: "Deployment",
      body: "Perception X2's nine AI engines execute the strategy across 50+ platforms. Deployment is autonomous, adaptive, and continuous. Human oversight ensures alignment with strategic objectives.",
      deliverables: ["Autonomous Engine Activation", "Cross-Platform Deployment", "Human Oversight Layer", "Real-Time Quality Control"],
    },
    {
      number: "04",
      title: "Optimization",
      body: "Real-time analytics drive continuous optimization. Our systems learn from performance data, adapting strategies to maximize impact. Every deployment improves over time.",
      deliverables: ["Performance Analytics", "Adaptive Strategy Refinement", "Quarterly Impact Reports", "Continuous Learning Loop"],
    },
  ],
  cta: [
    { label: "Explore Platform X2", href: "/platform/overview" },
    { label: "Contact Us", href: "/#contact" },
  ],
};

export interface TechnologyData {
  eyebrow: string;
  title: string;
  intro: string;
  whyProprietary: { heading: string; body: string }[];
  intellectualProperty: { label: string; value: string; detail: string }[];
  rdPipeline: { phase: string; description: string; status: "shipping" | "next" | "future" }[];
  cta: { label: string; href: string }[];
}

export const technology: TechnologyData = {
  eyebrow: "Our Technology",
  title: "Proprietary. Advanced. Unreplicable.",
  intro: "CryptoMize's technology stack is entirely proprietary. From the AI engines that power Perception X2 to the infrastructure that ensures 99.9999% uptime, every component is built, owned, and operated by CryptoMize.",
  whyProprietary: [
    {
      heading: "Why Proprietary Matters",
      body: "Off-the-shelf technology cannot deliver the outcomes Perception X2 achieves. Our engines are custom-built to process 144-dimensional emotional resonance data, simulate organic engagement patterns, and orchestrate autonomous deployment across 50+ platforms. This technology does not exist elsewhere.",
    },
    {
      heading: "Intellectual Property",
      body: "CryptoMize maintains a growing portfolio of patents, trade secrets, and proprietary methodologies. Our technology represents 15+ years of research and development — an investment no competitor can replicate through licensing or acquisition.",
    },
    {
      heading: "Continuous Innovation",
      body: "We invest heavily in R&D. Every quarter, new capabilities are added to Perception X2. Our innovation pipeline ensures that clients always have access to the most advanced perception amplification technology available.",
    },
  ],
  intellectualProperty: [
    { label: "Proprietary AI Engines", value: "9", detail: "Each addressing a specific perception dimension" },
    { label: "Patents & Filings", value: "60+", detail: "Active intellectual property portfolio" },
    { label: "Trade Secrets", value: "Hundreds", detail: "Refined through 15+ years of operation" },
    { label: "Codebase", value: "M+", detail: "Lines of bespoke engineering" },
  ],
  rdPipeline: [
    { phase: "Now Shipping", description: "Nine AI engines across 50+ platforms with 144-D emotional resonance mapping.", status: "shipping" },
    { phase: "Next Quarter", description: "Next-generation engine architecture with predictive intelligence layer.", status: "next" },
    { phase: "Future Roadmap", description: "Expanded ecosystem coverage, deeper autonomous operations, advanced cross-platform orchestration.", status: "future" },
  ],
  cta: [
    { label: "Explore the Platform", href: "/platform/overview" },
    { label: "Request a Demo", href: "/#contact" },
  ],
};

export interface CommitmentData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: {
    title: string;
    body: string;
    icon: string;
    accent: "crimson" | "denim" | "emerald" | "amber" | "purple";
  }[];
  cta: { label: string; href: string }[];
}

export const commitment: CommitmentData = {
  eyebrow: "Our Commitment",
  title: "To Our Clients. To Our Standards. To the Future.",
  intro: "CryptoMize is committed to delivering exceptional outcomes while maintaining the highest standards of ethics, security, and operational excellence.",
  pillars: [
    {
      title: "Ethical Deployment",
      body: "We believe in responsible perception amplification. Every capability we develop undergoes rigorous ethical review. We do not engage in deception, manipulation, or activities that undermine democratic institutions.",
      icon: "Scale",
      accent: "crimson",
    },
    {
      title: "Security",
      body: "We serve organizations where security is paramount. Our systems are hardened against intrusion. Our team members undergo security clearance. Our infrastructure meets or exceeds government-grade security standards.",
      icon: "ShieldCheck",
      accent: "denim",
    },
    {
      title: "Compliance",
      body: "We operate in compliance with all applicable regulations across every jurisdiction we serve. Our legal team ensures that every deployment meets regulatory requirements without exception.",
      icon: "FileCheck",
      accent: "emerald",
    },
    {
      title: "Innovation",
      body: "We commit to continuous improvement. The perception landscape evolves constantly, and CryptoMize evolves with it. Our R&D investment ensures that Perception X2 remains the most advanced platform available.",
      icon: "Sparkles",
      accent: "amber",
    },
  ],
  cta: [
    { label: "View Security", href: "/legal/security" },
    { label: "Contact Us", href: "/#contact" },
  ],
};

export interface GlobalOpsData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string; metric: string }[];
  byTheNumbers: { label: string; value: string }[];
  cta: { label: string; href: string }[];
}

export const globalOps: GlobalOpsData = {
  eyebrow: "Global Operations",
  title: "Multi-Continent. Multi-Timezone. Multi-Language.",
  intro: "CryptoMize operates across multiple continents, serving clients in 100+ languages. Our global infrastructure ensures consistent delivery regardless of geography, time zone, or language.",
  pillars: [
    {
      title: "Infrastructure",
      body: "Our platform infrastructure spans redundant data centers across multiple continents. Automatic failover, continuous monitoring, and disaster recovery protocols ensure 99.9999% uptime.",
      metric: "99.9999% Uptime",
    },
    {
      title: "Regional Operations",
      body: "Local teams provide market-specific expertise, cultural intelligence, and regional compliance knowledge. Global platform, local execution.",
      metric: "Multi-Continent",
    },
    {
      title: "Language Capabilities",
      body: "Perception X2 supports 100+ languages, enabling perception amplification in any market. Language capability is paired with cultural context to ensure narratives resonate authentically.",
      metric: "100+ Languages",
    },
  ],
  byTheNumbers: [
    { label: "Continents Operated", value: "Multi" },
    { label: "Languages Supported", value: "100+" },
    { label: "Data Centers", value: "Redundant" },
    { label: "Operating Since", value: "15+ Yrs" },
  ],
  cta: [
    { label: "Explore Operations", href: "/platform/overview" },
    { label: "Contact Us", href: "/#contact" },
  ],
};

export interface LeadershipData {
  eyebrow: string;
  title: string;
  intro: string;
  thoughtLeadership: { heading: string; body: string }[];
  recognition: { source: string; recognition: string; year: string }[];
  standards: { heading: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const leadership: LeadershipData = {
  eyebrow: "Industry Leadership",
  title: "Setting the Standard. Defining the Category.",
  intro: "CryptoMize did not enter the perception management industry — we created it. Our innovations have defined the category, our platform has set the standard, and our thought leadership has shaped the conversation.",
  thoughtLeadership: [
    {
      heading: "Thought Leadership",
      body: "CryptoMize executives are regular contributors to industry publications, conference presentations, and analyst briefings. Our insights on autonomous perception amplification are sought by organizations worldwide.",
    },
    {
      heading: "Industry Recognition",
      body: "Perception X2 has been recognized by leading technology research firms, industry analysts, and peer organizations. Our platform appears in industry reports covering AI-powered perception management.",
    },
    {
      heading: "Standards and Best Practices",
      body: "CryptoMize has contributed to the development of industry standards for perception management, AI ethics, and digital intelligence. We believe in raising the bar for the entire industry.",
    },
  ],
  recognition: [
    { source: "Technology Research", recognition: "Leading AI Perception Platform", year: "2025" },
    { source: "Industry Analysts", recognition: "Top Strategic Communications Provider", year: "2024" },
    { source: "Peer Organizations", recognition: "Innovation in Autonomous Systems", year: "2024" },
    { source: "Standards Bodies", recognition: "Contributor to AI Ethics Frameworks", year: "2023" },
  ],
  standards: [
    { heading: "Industry Standards", body: "Perception management, AI ethics, digital intelligence." },
    { heading: "Best Practices", body: "Operational excellence, security, transparency." },
  ],
  cta: [
    { label: "Read Insights", href: "/resources" },
    { label: "Contact Us", href: "/#contact" },
  ],
};

export interface CareersData {
  eyebrow: string;
  title: string;
  intro: string;
  whyJoin: { title: string; body: string; icon: string }[];
  openRoles: { area: string; count: string; focus: string }[];
  culture: { heading: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const careers: CareersData = {
  eyebrow: "Careers at CryptoMize",
  title: "Build What Others Cannot.",
  intro: "CryptoMize recruits exceptional minds who want to work on the most advanced perception amplification platform in existence. Our team members solve problems that do not exist at other companies.",
  whyJoin: [
    {
      title: "Proprietary Technology",
      body: "Work on systems that cannot be found elsewhere — nine engines refined over 15+ years.",
      icon: "Cpu",
    },
    {
      title: "Genuinely Hard Problems",
      body: "Solve challenges in AI, cognitive science, and strategic communications that have no off-the-shelf answers.",
      icon: "Brain",
    },
    {
      title: "Elite Specialists",
      body: "Join a team of specialists who demand excellence from themselves and each other.",
      icon: "Users",
    },
    {
      title: "World-Shaping Work",
      body: "Contribute to systems that shape how the world perceives reality — responsibly, measurably, ethically.",
      icon: "Globe",
    },
  ],
  openRoles: [
    { area: "Engineering", count: "Open", focus: "AI, ML, Distributed Systems, Platform" },
    { area: "Strategy", count: "Open", focus: "Perception, Narrative, Competitive Intelligence" },
    { area: "Operations", count: "Open", focus: "SRE, Security, Client Operations" },
    { area: "Creative Intelligence", count: "Open", focus: "Content, Linguistics, Cultural Analysis" },
  ],
  culture: [
    { heading: "Meritocratic", body: "The best ideas win, regardless of seniority or title." },
    { heading: "Mission-Aligned", body: "Every team member understands why the work matters." },
    { heading: "Continuously Learning", body: "Quarterly research time, conference attendance, ongoing education." },
  ],
  cta: [
    { label: "View Open Positions", href: "/careers" },
    { label: "Learn About Culture", href: "/company/culture" },
  ],
};

export interface AboutContactData {
  eyebrow: string;
  title: string;
  intro: string;
  promises: string[];
  nextSteps: { number: string; title: string; body: string }[];
  contactMethods: { label: string; email: string; note?: string }[];
}

export const aboutContact: AboutContactData = {
  eyebrow: "Contact",
  title: "Ready to Engage CryptoMize.",
  intro: "Whether you are exploring autonomous perception amplification for the first time or scaling an existing partnership, CryptoMize provides direct access to senior specialists.",
  promises: [
    "Executive-level attention on every inquiry",
    "Strict NDA protection on every consultation",
    "A conversation, not a sales pitch",
  ],
  nextSteps: [
    { number: "1", title: "Request a Demo", body: "See Perception X2 in action with a personalized demonstration." },
    { number: "2", title: "Book a Consultation", body: "Speak with a senior specialist about your objectives." },
    { number: "3", title: "Submit an Inquiry", body: "Ask questions, request information, or explore partnership opportunities." },
  ],
  contactMethods: [
    { label: "General Inquiries", email: "contact@perception.ac" },
    { label: "Sales & Demos", email: "sales@perception.ac", note: "Response within 24 hours" },
    { label: "Media & Press", email: "press@perception.ac" },
  ],
};

export interface TrustData {
  eyebrow: string;
  title: string;
  intro: string;
  trackRecord: { label: string; value: string; detail: string }[];
  transparencyCommitments: { heading: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const trust: TrustData = {
  eyebrow: "Trust and Transparency",
  title: "Why 300+ Organizations Trust CryptoMize.",
  intro: "Trust is not claimed — it is earned. CryptoMize has earned the trust of 300+ elite organizations through 15+ years of consistent delivery, absolute discretion, and measurable outcomes.",
  trackRecord: [
    { label: "Security Incidents", value: "0", detail: "Across 15+ years of operation" },
    { label: "Platform Uptime", value: "99.9999%", detail: "~31.5 seconds downtime per year" },
    { label: "Elite Clients", value: "300+", detail: "Across multiple continents" },
    { label: "Platform Coverage", value: "50+", detail: "Digital ecosystems monitored" },
    { label: "Language Support", value: "100+", detail: "Localized perception amplification" },
  ],
  transparencyCommitments: [
    {
      heading: "Real-Time Status",
      body: "We publish our platform status in real time. Clients always know the state of the systems serving them.",
    },
    {
      heading: "Honest Incident Reporting",
      body: "We report incidents honestly and completely. No minimization, no spin, no delay.",
    },
    {
      heading: "Measurable Outcomes",
      body: "We provide measurable outcomes in every engagement. Numbers, not narratives. Verifiable, not aspirational.",
    },
  ],
  cta: [
    { label: "View Platform Status", href: "/status" },
    { label: "Contact Us", href: "/#contact" },
  ],
};

export interface FutureData {
  eyebrow: string;
  title: string;
  intro: string;
  roadmap: { phase: string; title: string; items: string[] }[];
  marketExpansion: { heading: string; body: string }[];
  longTermVision: { quote: string; body: string };
  cta: { label: string; href: string }[];
}

export const future: FutureData = {
  eyebrow: "Future Vision",
  title: "Where CryptoMize Is Going.",
  intro: "CryptoMize is building the future of autonomous perception amplification. Our roadmap includes next-generation AI engines, expanded platform coverage, and deeper integration capabilities.",
  roadmap: [
    {
      phase: "Q1 2026",
      title: "Next-Generation Engine Architecture",
      items: [
        "Next-generation AI engine architecture",
        "Enhanced 144-dimensional emotional resonance mapping",
      ],
    },
    {
      phase: "Q2 2026",
      title: "Expanded Ecosystem Coverage",
      items: [
        "Expanded platform coverage beyond 50+ ecosystems",
        "Deeper autonomous operations capabilities",
      ],
    },
    {
      phase: "Q3 2026",
      title: "Predictive Intelligence",
      items: [
        "Advanced predictive intelligence",
        "Real-time market sentiment forecasting",
      ],
    },
  ],
  marketExpansion: [
    { heading: "New Industries", body: "Verticals we have not yet served — bringing perception amplification to organizations that have never had access." },
    { heading: "New Geographies", body: "Markets where perception matters but tooling is absent — extending our global footprint." },
    { heading: "New Use Cases", body: "Applications beyond our current scope — discovering new ways perception engineering can serve organizations." },
  ],
  longTermVision: {
    quote: "A world where every organization has access to the perception amplification capabilities that were once reserved for the largest and most well-resourced entities.",
    body: "CryptoMize is building toward that future. Our platform's capabilities grow alongside the organizations we serve.",
  },
  cta: [
    { label: "Explore the Platform", href: "/platform/overview" },
    { label: "Contact Us", href: "/#contact" },
  ],
};

export interface LegalData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string; icon: string; accent: "crimson" | "denim" | "emerald" }[];
  cta: { label: string; href: string }[];
}

export const legal: LegalData = {
  eyebrow: "Legal and Compliance",
  title: "Operating Within the Law. Always.",
  intro: "CryptoMize operates in full compliance with all applicable laws, regulations, and ethical standards across every jurisdiction we serve.",
  pillars: [
    {
      title: "Regulatory Compliance",
      body: "We maintain compliance with data protection regulations, communication standards, and platform policies in every market we operate. Our legal team ensures ongoing compliance as regulations evolve.",
      icon: "FileCheck",
      accent: "denim",
    },
    {
      title: "Ethical Standards",
      body: "Every capability we develop undergoes ethical review. We do not engage in activities that are illegal, deceptive, or harmful to democratic institutions. Our commitment to ethical deployment is non-negotiable.",
      icon: "Scale",
      accent: "crimson",
    },
    {
      title: "Audit and Accountability",
      body: "We welcome scrutiny. Our operations are regularly audited. Our outcomes are independently verifiable. Accountability is not a burden at CryptoMize — it is a competitive advantage.",
      icon: "Eye",
      accent: "emerald",
    },
  ],
  cta: [
    { label: "View Security", href: "/legal/security" },
    { label: "Legal Policies", href: "/legal" },
  ],
};

export interface GetStartedData {
  eyebrow: string;
  title: string;
  intro: string;
  steps: { number: string; title: string; body: string }[];
  promises: string[];
  ctaPrimary: { label: string; href: string };
  ctaSecondary: { label: string; href: string };
}

export const getStarted: GetStartedData = {
  eyebrow: "Get Started",
  title: "Your Perception Transformation Begins Here.",
  intro: "CryptoMize invites you to discover what autonomous perception amplification can achieve for your organization. Whether you need reputation protection, narrative amplification, competitive positioning, or comprehensive perception management, Perception X2 delivers.",
  steps: [
    { number: "01", title: "Request a Demo", body: "See Perception X2 in action with a personalized demonstration" },
    { number: "02", title: "Book a Consultation", body: "Speak with a senior specialist about your objectives" },
    { number: "03", title: "Submit an Inquiry", body: "Ask questions, request information, or explore partnership opportunities" },
  ],
  promises: [
    "Every engagement begins with a conversation",
    "Every conversation is protected by NDA",
    "Every outcome is measured, reported, and optimized",
  ],
  ctaPrimary: { label: "Request a Demo", href: "/#contact" },
  ctaSecondary: { label: "Explore the Platform", href: "/platform/overview" },
};
